import type { HazardsConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import { HazardKind } from './HazardDefinition';
import type { HazardCue, HazardStepContext } from './HazardModel';
import { createHazardModel, HazardInstance } from './HazardInstance';

/** Lower bound for the ETA division (the run speed is never 0, but a test may pass it). */
const MIN_SPEED = 1e-3;

/** Step context of the hazard currently being stepped. */
class StepContext implements HazardStepContext {
  armed = false;
  segmentStart = 0;
  playerDistance = 0;
  speed = 0;

  eta(localZ: number): number {
    return (this.segmentStart + localZ - this.playerDistance) / Math.max(this.speed, MIN_SPEED);
  }
}

/**
 * Spawns the moving hazards of a segment's spawn plan, arms them through their dynamic event
 * socket and steps their timelines (before the RunDirector, so collisions see this step's
 * colliders). Pure gameplay – HazardPresenter animates the visuals, CollisionSystem queries the
 * colliders, audio/effects listen to `hazard:cue`.
 */
export class HazardManager {
  private readonly bySegment = new Map<number, HazardInstance[]>();
  private nextUid = 1;
  private readonly cues: HazardCue[] = [];
  private readonly ceilingScratch: HazardInstance[] = [];
  private readonly ctx = new StepContext();

  constructor(
    private readonly config: HazardsConfig,
    private readonly world: WorldConfig,
    private readonly events: EventBus<GameEvents>,
  ) {
    events.on('segment:spawned', ({ segment }) => this.spawnFor(segment));
    events.on('segment:recycled', ({ segment }) => this.despawnFor(segment));
    events.on('dynamicEvent:armed', ({ socket }) => {
      for (const hazard of this.bySegment.get(socket.segment.uid) ?? []) {
        if (hazard.definition.socketId === socket.socket.id) hazard.armed = true;
      }
    });
  }

  /** Advances all hazards of committed segments (fixed step). */
  update(dt: number, playerDistance: number, speed: number): void {
    const ctx = this.ctx;
    ctx.playerDistance = playerDistance;
    ctx.speed = speed;
    for (const list of this.bySegment.values()) {
      for (const hazard of list) {
        if (!hazard.segment.committed) continue;
        ctx.armed = hazard.armed;
        ctx.segmentStart = hazard.segment.startDistance;
        this.cues.length = 0;
        hazard.step(dt, ctx, this.cues);
        for (const cue of this.cues) this.events.emit('hazard:cue', { hazard, cue });
      }
    }
  }

  /** Hazards on committed segments that still collide (or all, with `includeSpared`) and whose bounds overlap [minZ, maxZ]. */
  query(minZ: number, maxZ: number, out: HazardInstance[] = [], includeSpared = false): HazardInstance[] {
    out.length = 0;
    for (const list of this.bySegment.values()) {
      for (const hazard of list) {
        if ((hazard.spared && !includeSpared) || !hazard.segment.committed) continue;
        if (hazard.bounds.maxZ > minZ && hazard.bounds.minZ < maxZ) out.push(hazard);
      }
    }
    return out;
  }

  /**
   * Lowest underside of hazard colliders (bottom ≥ minHeight) over the footprint – slide hold and
   * camera. Beams the runner got past with protection still hang there (the camera ducks under them).
   */
  lowestCeiling(minZ: number, maxZ: number, minX: number, maxX: number, minHeight: number): number | null {
    let ceiling: number | null = null;
    for (const hazard of this.query(minZ, maxZ, this.ceilingScratch, true)) {
      if (hazard.spared && hazard.definition.kind !== HazardKind.CraneBeam) continue;
      for (let i = 0; i < hazard.trackColliders.length; i++) {
        const c = hazard.trackColliders[i]!;
        if (!hazard.enabled[i] || c.minY < minHeight || c.maxZ <= minZ || c.minZ >= maxZ || c.maxX <= minX || c.minX >= maxX) continue;
        ceiling = ceiling === null ? c.minY : Math.min(ceiling, c.minY);
      }
    }
    return ceiling;
  }

  /** All spawned hazards (including spared ones and those on uncommitted previews). */
  all(): HazardInstance[] {
    return [...this.bySegment.values()].flat();
  }

  /** The runner got past it (protected, or a life was lost on it): no more collisions with it. */
  spare(hazard: HazardInstance): void {
    if (hazard.spared) return;
    hazard.spared = true;
    this.events.emit('hazard:spared', { hazard });
  }

  reset(): void {
    this.ctx.playerDistance = 0;
    this.ctx.speed = 0;
  }

  private spawnFor(segment: SegmentInstance): void {
    const definitions = segment.definition.hazards ?? [];
    const list: HazardInstance[] = [];
    for (const id of segment.spawnPlan.hazards) {
      const definition = definitions.find((h) => h.id === id);
      if (!definition) throw new Error(`${segment.definition.id}: unknown hazard ${id}`);
      const hazard = new HazardInstance(this.nextUid++, definition, segment, createHazardModel(definition, this.config, this.world));
      list.push(hazard);
      this.events.emit('hazard:spawned', { hazard });
    }
    if (list.length > 0) this.bySegment.set(segment.uid, list);
  }

  private despawnFor(segment: SegmentInstance): void {
    const list = this.bySegment.get(segment.uid);
    if (!list) return;
    for (const hazard of list) this.events.emit('hazard:despawned', { hazard });
    this.bySegment.delete(segment.uid);
  }
}
